import React from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { SunIcon, MoonIcon, ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { useSearchParams } from "react-router-dom";
import logo from "../Landing Page/Stand-logo 1.png";
import logoWhite from "../../Images/Stande-logo-white.png";
import { a } from "../appColors";
import { recaptchaEnabled } from "../recaptcha";
import { STANDE, STANDE_DARK, GREY } from "../../StandeClassroom/standeTokens";
import "../app-theme.css";

/**
 * What the login and sign-up pages share: the two ways in, the page around
 * them, and the form controls. Both pages start with the same choice, so
 * they draw it from the same place.
 */

// `userType` is what the account is (and, on login, must already be).
export const ROLES = {
  student: {
    userType: "STUDENT",
    title: "Student",
    article: "a student",
    blurb: "Solve past papers, get marked and track your progress.",
  },
  instructor: {
    userType: "INSTRUCTOR",
    title: "Instructor",
    article: "an instructor",
    blurb: "Run your spaces, set work and review your students' answers.",
  },
};

export const roleForUserType = (userType) => (userType === "INSTRUCTOR" ? "instructor" : "student");

/** The chosen role, carried in `?as=` so the back button returns to the choice. */
export function useRoleParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const role = ROLES[searchParams.get("as")] ? searchParams.get("as") : null;
  const chooseRole = (next) => setSearchParams(next ? { as: next } : {});
  return [role, chooseRole];
}

// Primary CTA — the brand accent, as on SolvePaper
export const primaryButtonStyle = {
  display: "flex",
  padding: "10px 24px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  background: a.accent,
  color: a.onAccent,
  fontSize: "14px",
  fontWeight: "500",
  lineHeight: "normal",
  width: "100%",
  cursor: "pointer",
  transition: "background 0.2s",
  _hover: { background: a.accentHover },
};

// Secondary CTA — a quiet surface button
export const secondaryButtonStyle = {
  display: "flex",
  padding: "10px 24px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  background: a.surface,
  color: a.text,
  border: "1px solid",
  borderColor: a.border,
  fontSize: "14px",
  fontWeight: "500",
  lineHeight: "normal",
  width: "100%",
  cursor: "pointer",
  transition: "background 0.2s",
  _hover: { background: a.hover },
};

// Cyan focus ring shared by the inputs
export const inputSx = {
  width: "100%",
  height: "45px",
  borderRadius: "14px",
  border: "1px solid",
  borderColor: a.inputBorder,
  background: a.inputBg,
  color: a.text,
  boxShadow: "0px 0px 5.9px 0px rgba(0, 0, 0, 0.05)",
  _hover: { borderColor: "#CBD5D1" },
  _focus: {
    borderColor: "#00C9FE",
    boxShadow: "0 0 0 1px #00C9FE",
  },
  // Keep the native date picker legible in dark mode.
  "::-webkit-calendar-picker-indicator": { filter: "var(--app-picker-filter, none)" },
};

/** A labelled input wired to a Formik field, with its error underneath. */
export function AuthField({ formik, name, label, type = "text", hint, autoComplete, rightElement }) {
  const error = formik.touched[name] && formik.errors[name];
  const input = (
    <Input
      type={type}
      name={name}
      autoComplete={autoComplete}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      value={formik.values[name]}
      sx={inputSx}
    />
  );
  return (
    <FormControl isInvalid={!!error} textAlign="left">
      <FormLabel color={a.text} fontSize="14px" fontWeight="400">
        {label}
      </FormLabel>
      {rightElement ? (
        <InputGroup>
          {input}
          <InputRightElement width="4.5rem" h="45px">
            {rightElement}
          </InputRightElement>
        </InputGroup>
      ) : (
        input
      )}
      {error ? (
        <Text color="red.400" fontSize="sm" mt="4px">
          {error}
        </Text>
      ) : (
        hint && (
          <Text color={a.textMuted} fontSize="12px" fontWeight="400" mt="6px">
            {hint}
          </Text>
        )
      )}
    </FormControl>
  );
}

/** The eye button for a password field. */
export function RevealButton({ shown, onToggle }) {
  return (
    <Button
      h="1.75rem"
      size="sm"
      onClick={onToggle}
      aria-label={shown ? "Hide password" : "Show password"}
      sx={{
        bg: "none",
        color: a.text2,
        _hover: { bg: "none", boxShadow: "none" },
        _focus: { boxShadow: "none" },
        _active: { bg: "none", boxShadow: "none" },
      }}
    >
      {shown ? <ViewOffIcon /> : <ViewIcon />}
    </Button>
  );
}

/** A problem worth a box rather than a line: the red tint, as elsewhere. */
export function AuthNotice({ tone = "bad", title, children }) {
  return (
    <Box
      padding="14px 18px"
      borderRadius="14px"
      border="1px solid"
      borderColor={a.border}
      background={tone === "warn" ? a.warnBg : a.badBg}
      textAlign="left"
    >
      {title && (
        <Text fontSize="15px" fontWeight="500" color={a.text}>
          {title}
        </Text>
      )}
      <Text fontSize="13px" fontWeight="400" color={tone === "warn" ? a.textSub : a.badText} mt={title ? "2px" : 0}>
        {children}
      </Text>
    </Box>
  );
}

// One way in: a brand-coloured tile, the role and what it's for, and an arrow
// to go on. Both cards sit on the page's surface; the tile colour tells them
// apart, and the warm ground only comes in on hover.
function RoleCard({ role, onChoose, isDark }) {
  const r = ROLES[role];
  const brand = isDark ? STANDE_DARK : STANDE;
  const isInstructor = role === "instructor";
  return (
    <Button
      onClick={() => onChoose(role)}
      variant="unstyled"
      role="group"
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      w="100%"
      h="auto"
      minH={{ base: "176px", md: "208px" }}
      p={{ base: "20px", md: "24px" }}
      pb={{ base: "56px", md: "60px" }}
      textAlign="left"
      whiteSpace="normal"
      borderRadius="10px"
      border="1px solid"
      borderColor={isDark ? GREY[800] : STANDE.black}
      bg={a.surface}
      transition="transform 0.2s, box-shadow 0.2s, background 0.2s"
      _hover={{
        bg: a.surfaceSubtle,
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 12px 28px -14px rgba(0,0,0,0.8)"
          : "0 12px 28px -14px rgba(34,34,34,0.35)",
      }}
      _active={{ transform: "translateY(0)" }}
      _focusVisible={{ boxShadow: `0 0 0 3px ${brand.primaryBlue}` }}
    >
      <Box
        aria-hidden
        boxSize={{ base: "60px", md: "70px" }}
        borderRadius="16px"
        bg={isInstructor ? brand.secondaryOrange : brand.primaryBlue}
      />
      <Text fontSize="16px" fontWeight="500" color={a.text} mt="14px">
        {r.title}
      </Text>
      <Text fontSize="14px" fontWeight="400" color={a.text2} mt="8px" maxW="260px" lineHeight="1.45">
        {r.blurb}
      </Text>
      <Flex
        position="absolute"
        right={{ base: "16px", md: "18px" }}
        bottom={{ base: "16px", md: "18px" }}
        align="center"
        justify="center"
        boxSize="28px"
        borderRadius="full"
        bg={a.ink}
        color={a.onInk}
        transition="transform 0.2s"
        _groupHover={{ transform: "translateX(3px)" }}
      >
        <Icon as={ArrowRightIcon} w="14px" h="14px" strokeWidth={2.5} />
      </Flex>
    </Button>
  );
}

/** The first step of both pages: student or instructor. */
export function RoleChooser({ heading, subheading, onChoose, isDark }) {
  return (
    <>
      <Heading mb="6px" color={a.text} fontSize={{ base: "24px", md: "28px" }} fontWeight="600" textAlign="center">
        {heading}
      </Heading>
      <Text color={a.textSub} fontSize="15px" fontWeight="300" mb={{ base: 6, md: 10 }} textAlign="center">
        {subheading}
      </Text>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={{ base: "12px", md: "18px" }}>
        <RoleCard role="student" onChoose={onChoose} isDark={isDark} />
        <RoleCard role="instructor" onChoose={onChoose} isDark={isDark} />
      </Grid>
    </>
  );
}

/** "Not an instructor? Change" — back to the choice. */
export function ChangeRole({ role, onClick }) {
  return (
    <Button
      onClick={onClick}
      variant="unstyled"
      display="inline-flex"
      alignItems="center"
      gap="6px"
      h="auto"
      mb="14px"
      fontSize="13px"
      fontWeight="500"
      color={a.textSub}
      _hover={{ color: a.text }}
    >
      <Icon as={ArrowLeftIcon} w="14px" h="14px" />
      Not {ROLES[role].article}? Change
    </Button>
  );
}

/** An inline link in the page's accent. */
export function AccentLink({ children }) {
  return (
    <Text as="span" color={a.accent} fontWeight="500" _hover={{ textDecoration: "underline" }}>
      {children}
    </Text>
  );
}

/**
 * The attribution Google requires in place of the reCAPTCHA badge, which is
 * hidden site-wide (see .grecaptcha-badge in index.css). Shown under the forms
 * that actually use reCAPTCHA, and only when it is switched on.
 */
export function RecaptchaNotice() {
  if (!recaptchaEnabled) return null;
  return (
    <Text color={a.textSub} fontSize="12px" mt={3} textAlign="center">
      This site is protected by reCAPTCHA and the Google{" "}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
        <AccentLink>Privacy Policy</AccentLink>
      </a>{" "}
      and{" "}
      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
        <AccentLink>Terms of Service</AccentLink>
      </a>{" "}
      apply.
    </Text>
  );
}

/**
 * The page around both steps: the theme toggle, the logo and a centred
 * column — wide for the two cards, form-width once a role is chosen.
 */
export function AuthPage({ isDark, toggle, wide, centered = wide, children }) {
  return (
    <Flex
      className="stande-app"
      data-theme={isDark ? "dark" : "light"}
      position="relative"
      minHeight="100vh"
      backgroundColor={a.page}
      sx={{ "--app-picker-filter": isDark ? "invert(1)" : "none" }}
    >
      <Button
        onClick={toggle}
        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
        position="absolute"
        top={{ base: "16px", md: "24px" }}
        right={{ base: "16px", md: "24px" }}
        variant="unstyled"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minW="38px"
        w="38px"
        h="38px"
        borderRadius="full"
        color={a.text2}
        _hover={{ bg: a.hover }}
        zIndex="1"
      >
        <Icon as={isDark ? SunIcon : MoonIcon} w="20px" h="20px" />
      </Button>
      <Box flex="1" display="flex" justifyContent="center" alignItems="center" px="20px" py={{ base: "64px", md: "48px" }}>
        <Box width={{ base: "100%", md: wide ? "740px" : "429px" }} textAlign={{ base: "center", md: "left" }}>
          <Flex mb={{ base: "20px", md: "45px" }} justifyContent={{ base: "center", md: centered ? "center" : "left" }}>
            <Image src={isDark ? logoWhite : logo} alt="Stande" width={{ base: "110px", md: "125px" }} />
          </Flex>
          {children}
        </Box>
      </Box>
    </Flex>
  );
}
